import { skipToken, useSuspenseQuery } from "@apollo/client";

import type {
	ApolloError,
	ApolloQueryResult,
	DocumentNode,
	NoInfer,
	OperationVariables,
	SuspenseQueryHookOptions,
	TypedDocumentNode,
} from "@apollo/client";

export type QuerySideEffectHandler = (props: { error: ApolloError | undefined }) => void;

export const customSuspenseQuery =
	(sideEffectHandler: QuerySideEffectHandler) =>
	<
		TData, //
		TVariables extends OperationVariables,
		TOptions extends Omit<SuspenseQueryHookOptions<TData>, "variables">,
	>(
		query: DocumentNode | TypedDocumentNode<TData, TVariables>,
		options?: SuspenseQueryHookOptions<NoInfer<TData>, NoInfer<TVariables>> & TOptions,

		/**
		 * skipToken = true の場合、suspense でのデータ取得をスキップする
		 * usecase としては、ユーザーアクション起因でのデータ取得の場合に利用する（refetch でのデータ取得）
		 * @example
		 * ```
		 * const _ = useCustomSuspenseQuery(query, options, true /* skipToken = true * /);
		 * ```
		 */
		skip?: boolean,
	): {
		data: TData | undefined;
		refetch: (variables?: Partial<TVariables> | undefined) => Promise<ApolloQueryResult<TData | undefined>>;
		error: ApolloError | undefined;
	} => {
		const { data, error, refetch } = useSuspenseQuery(query, skip ? skipToken : { ...options, errorPolicy: "all" });

		sideEffectHandler({ error });

		return { data, refetch, error };
	};
